
/**
 * 🔒 SECURE SUPABASE CLIENT - V145 PRODUCTION HARDENING
 * Uses production-safe constants to eliminate environment variable failures
 * Ensures reliable database connectivity in all deployment environments
 */

import { createClient } from '@supabase/supabase-js';
import { requestTracker } from '@/utils/requestTracker';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL?.trim();
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY?.trim();
if (!supabaseUrl) {
  throw new Error('[BlissNet] Missing VITE_SUPABASE_URL');
}
if (!supabaseAnonKey) {
  throw new Error('[BlissNet] Missing VITE_SUPABASE_ANON_KEY');
}

// V157.6-HOTFIX-3: Universal smart chainable factory
function createSmartChainable(cached: any) {
  let singleRequested = false;
  let maybeSingleRequested = false;

  const chainable: any = {
    then: (resolve: any) => {
      if (singleRequested || maybeSingleRequested) {
        const data = cached?.data;

        if (Array.isArray(data)) {
          if (data.length === 0) {
            return resolve(singleRequested ?
              { data: null, error: { code: 'PGRST116', message: '0 rows', details: 'Results contain 0 rows' } } :
              { data: null, error: null }
            );
          } else if (data.length === 1) {
            return resolve({ ...cached, data: data[0] });
          } else {
            return resolve(singleRequested ?
              { data: null, error: { code: 'PGRST116', message: `${data.length} rows`, details: `Results contain ${data.length} rows` } } :
              { ...cached, data: data[0] }
            );
          }
        }
      }
      return resolve(cached);
    },

    catch: (reject: any) => ({ then: (r: any) => r(cached) }),

    // All chaining methods return chainable
    eq: () => chainable,
    neq: () => chainable,
    gt: () => chainable,
    gte: () => chainable,
    lt: () => chainable,
    lte: () => chainable,
    in: () => chainable,
    is: () => chainable,
    like: () => chainable,
    ilike: () => chainable,
    match: () => chainable,
    not: () => chainable,
    or: () => chainable,        // ← ADD THIS
    and: () => chainable,       // ← ADD THIS
    filter: () => chainable,
    order: () => chainable,
    limit: () => chainable,
    range: () => chainable,
    contains: () => chainable,  // ← ADD THIS
    containedBy: () => chainable, // ← ADD THIS

    // Smart single/maybeSingle
    single: () => {
      singleRequested = true;
      return chainable;
    },
    maybeSingle: () => {
      maybeSingleRequested = true;
      return chainable;
    }
  };

  return chainable;
}

// 🔒 SECURITY: Secure Supabase client configuration
// V157.6-LITE: Create base client
// V167.10: Export base client for critical queries that need to bypass request tracker
export const baseSupabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
    // 🔒 SECURITY: Enhanced session security
    storageKey: 'blissnet-auth',
    storage: {
      getItem: (key: string) => {
        try {
          const item = localStorage.getItem(key);
          return item ? JSON.parse(item) : null;
        } catch {
          return null;
        }
      },
      setItem: (key: string, value: string) => {
        try {
          localStorage.setItem(key, JSON.stringify(value));
        } catch (error) {
          console.warn('Failed to store auth session:', error);
        }
      },
      removeItem: (key: string) => {
        localStorage.removeItem(key);
      }
    }
  },
  global: {
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'X-Client-Info': 'blissnet-secure-v99'
    }
  },
  db: {
    schema: 'public'
  },
  realtime: {
    params: {
      eventsPerSecond: 10
    }
  }
});

// V157.6-LITE: Nuclear Supabase Query Interception
// Wrap the base client with Proxy to track ALL queries
const ENABLE_QUERY_TRACKING = true; // Always enabled for V157.6-LITE

// Critical queries that should never be blocked (auth, critical data)
const CRITICAL_QUERIES = [
  'users.select(*)',
  'users.select(id',
  'users.select(count',
  'auth.users',
  'profiles.select(*)',
  'auth_sessions'
];

// V157.6-LITE: Export wrapped client as default
const supabase = ENABLE_QUERY_TRACKING
  ? new Proxy(baseSupabase, {
      get(target: any, prop: string) {
        // Intercept .from() calls
        if (prop === 'from') {
          return (table: string) => {
            const queryBuilder = target.from(table);

            // Wrap the select method
            const originalSelect = queryBuilder.select.bind(queryBuilder);

            queryBuilder.select = function(...args: any[]) {
              const selectQuery = args[0] || '*';
              const queryKey = `supabase.${table}.select(${selectQuery})`;

              // Check if this is a critical query
              const isCritical = CRITICAL_QUERIES.some(pattern =>
                queryKey.includes(pattern)
              );

              if (isCritical) {
                // V159.2.5: Silenced critical query spam
                // console.log('🔓 V157.6-LITE: Critical query, allowing:', table);
                return originalSelect(...args);
              }

              // V157.6-NUCLEAR: Block unfiltered users table queries
              if (table === 'users' && selectQuery.includes('photo_urls')) {
                // V159.2.4: Silenced interception warning + stack trace spam
                // console.warn('🚨 V157.6-NUCLEAR: Intercepted photo_urls query');

                // Track this to detect if it has filters
                let hasUserFilter = false;

                // Create wrapped builder that tracks if eq/in/filter is called
                const builder = originalSelect(...args);

                return new Proxy(builder, {
                  get(target: any, prop: string) {
                    const method = target[prop];

                    // Track filter methods
                    if (prop === 'eq' || prop === 'in' || prop === 'filter') {
                      hasUserFilter = true;
                      // V159.2.4: Silenced filter confirmation spam
                      // console.log('✅ V157.6-NUCLEAR: Filter applied to photo_urls query');
                      return function(...filterArgs: any[]) {
                        return method.apply(target, filterArgs);
                      };
                    }

                    // Intercept execution
                    if (prop === 'then' || prop === 'single' || prop === 'maybeSingle') {
                      return function(...execArgs: any[]) {
                        if (!hasUserFilter) {
                          console.error('❌ V157.6-NUCLEAR: BLOCKING unfiltered photo_urls query!');

                          // Return empty result instead of executing
                          if (prop === 'then') {
                            const resolve = execArgs[0];
                            return Promise.resolve({
                              data: null,
                              error: {
                                message: 'Blocked: photo_urls query must include user ID filter',
                                code: 'BLOCKED_UNFILTERED'
                              }
                            }).then(resolve);
                          }

                          // For single/maybeSingle, return chainable that resolves to empty
                          return createSmartChainable({ data: null, error: null });
                        }

                        // Query has filter, allow it
                        console.log('✅ V157.6-NUCLEAR: Allowing filtered photo_urls query');
                        return method.apply(target, execArgs);
                      };
                    }

                    return method;
                  }
                });
              }

              // V157.6-ULTIMATE: Check if query already in progress
              const tracker = requestTracker as any;
              const metadata = tracker.requests?.get(queryKey);

              if (metadata?.inProgress) {
                console.log('🚫 V157.6-ULTIMATE: Duplicate in-progress query blocked:', {
                  table,
                  query: selectQuery.toString().substring(0, 50)
                });

                // Return promise that resolves when original completes
                return new Promise((resolve) => {
                  const waitForCompletion = setInterval(() => {
                    const current = tracker.requests?.get(queryKey);
                    if (!current?.inProgress && current?.result) {
                      clearInterval(waitForCompletion);
                      console.log('💾 V157.6-ULTIMATE: Original completed, returning result for:', table);
                      resolve(createSmartChainable(current.result)); // ← USE FACTORY
                    }
                  }, 50); // Check every 50ms

                  // V167.8: Increased timeout from 3s to 10s for critical queries (matches, connections)
                  const timeoutMs = (table === 'matches' || table === 'connections') ? 10000 : 3000;
                  setTimeout(() => {
                    clearInterval(waitForCompletion);
                    console.warn(`⏰ V167.8: Timeout waiting for original (${timeoutMs}ms), returning empty for:`, table);
                    resolve(createSmartChainable({ data: null, error: null })); // ← USE FACTORY
                  }, timeoutMs);
                });
              }

              // V167.8: Reduced cooldown for critical tables (matches: 30s, others: 5min)
              const cooldownMs = (table === 'matches') ? 30000 : 300000;
              if (!requestTracker.canExecute(queryKey, cooldownMs)) {
                console.log('🚫 V167.8: Blocked duplicate query:', {
                  table,
                  query: selectQuery.toString().substring(0, 50),
                  cooldown: `${cooldownMs / 1000}s`
                });

                // Try to return cached result
                const cached = requestTracker.getCachedResult(queryKey, cooldownMs);
                if (cached) {
                  console.log('💾 V167.8: Returning cached result for:', table);
                  return createSmartChainable(cached); // ← USE FACTORY
                }

                // No cache, return empty but valid response
                console.warn('⚠️ V167.8: No cache, returning empty for:', table);
                return createSmartChainable({ data: null, error: null }); // ← USE FACTORY
              }

              // V159.2.5: Silenced query execution spam
              // V157.6-LITE: Allow query and track it
              // console.log('⏱️ V157.6-LITE: Executing query:', {
              //   table,
              //   query: selectQuery.toString().substring(0, 50),
              //   fullKey: queryKey,
              //   args: args
              // });

              // V159.2.1: Reduced log spam - commented out verbose photo_urls debugging
              // V157.6-HOTFIX-3: Log stack trace for photo_urls queries to identify source
              // if (queryKey.includes('photo_urls')) {
              //   console.log('📍 V157.6-HOTFIX-3: PHOTO_URLS QUERY STACK TRACE:');
              //   console.trace();
              // }

              // V157.6-HOTFIX-3: Special debug for photo_urls queries
              if (queryKey.includes('photo_urls')) {
                // V159.2.1: Silenced debug log spam
                // console.log('🔍 V157.6-HOTFIX-3: PHOTO_URLS QUERY DETECTED:', {
                //   table,
                //   queryKey,
                //   selectQuery: selectQuery.toString(),
                //   args: JSON.stringify(args)
                // });

                // V157.6-HOTFIX-3: Check for undefined user ID in photo_urls queries
                const hasUndefinedId = args.some((arg: any) =>
                  arg && typeof arg === 'object' &&
                  ('id' in arg || 'userId' in arg) &&
                  (arg.id === undefined || arg.id === null || arg.userId === undefined || arg.userId === null)
                );

                if (hasUndefinedId) {
                  console.error('❌ V157.6-HOTFIX-3: UNDEFINED USER ID DETECTED - BLOCKING QUERY:', {
                    table,
                    queryKey,
                    args: JSON.stringify(args)
                  });

                  // Return empty result to prevent unconstrained query
                  return Promise.resolve(createSmartChainable({ data: null, error: {
                    code: 'BLOCKED_UNDEFINED_ID',
                    message: 'Query blocked due to undefined user ID'
                  }}));
                }

                // V159.2.1: Silenced non-critical warning
                // V157.6-HOTFIX-3: Additional check for queries that might return multiple rows
                // if (queryKey.includes('photo_urls') && !queryKey.includes('limit') && !queryKey.includes('in(')) {
                //   console.warn('⚠️ V157.6-HOTFIX-3: Photo_urls query without explicit limit - potential multi-row result');
                // }
              }

              requestTracker.startRequest(queryKey);

              // Execute original query
              const promise = originalSelect(...args);

              // Track completion - V167.FINAL: pass result on error so waiters resolve (no 10s stall)
              promise
                .then((result: any) => {
                  requestTracker.completeRequest(queryKey, result);
                  return result;
                })
                .catch((error: any) => {
                  requestTracker.completeRequest(queryKey, { data: null, error });
                  console.error('❌ V157.6-LITE: Query failed:', table, error);
                  throw error;
                });

              return promise;
            };

            return queryBuilder;
          };
        }

        // For all other methods, pass through unchanged
        return target[prop];
      }
    })
  : baseSupabase; // Use untracked in case tracking is disabled

// V157.6-LITE: Export original client for emergency use
export const supabaseUntracked = baseSupabase;

// V157.6-LITE: Expose for debugging
if (typeof window !== 'undefined') {
  (window as any).__supabaseUntracked = baseSupabase;
  (window as any).__requestTracker = requestTracker;
}

// Test basic database connectivity
export const testBasicConnectivity = async () => {
  try {
    
    // Test basic connection by querying users table count
    const { data, error } = await baseSupabase
      .from('users')
      .select('count')
      .limit(1);
    
    if (error) {
      console.error('❌ Database connectivity test failed:', error);
      return { 
        success: false, 
        error: error.message,
        timestamp: new Date().toISOString()
      };
    }
    
    console.log('✅ Database connectivity test passed');
    return { 
      success: true, 
      data: data,
      timestamp: new Date().toISOString()
    };
  } catch (err) {
    console.error('❌ Database connectivity test error:', err);
    return { 
      success: false, 
      error: err instanceof Error ? err.message : 'Unknown error',
      timestamp: new Date().toISOString()
    };
  }
};

// Simplified database connectivity test
export const testDatabaseConnectivity = async (): Promise<{ success: boolean; error?: string }> => {
  try {
    const { data, error } = await baseSupabase.from('users').select('count').limit(1);

    if (error) {
      console.error('Database connectivity failed:', error.message);
      return { success: false, error: error.message };
    }

    return { success: true };

  } catch (error: any) {
    console.error('Database connectivity exception:', error.message);
    return { success: false, error: error.message };
  }
};

// Simplified auth context verification
export const ensureAuthContext = async (): Promise<{ success: boolean; session: any; error?: string }> => {
  try {
    const { data: { session }, error: sessionError } = await baseSupabase.auth.getSession();

    if (sessionError) {
      console.error('Session error:', sessionError);
      return { success: false, session: null, error: sessionError.message };
    }

    if (!session) {
      return { success: false, session: null, error: 'No active session' };
    }

    return { success: true, session };

  } catch (error: any) {
    console.error('❌ AUTH-FIX: Auth context exception:', error);
    return { success: false, session: null, error: error.message };
  }
};

// Simplified auth-aware query wrapper
export const authAwareQuery = async (queryFn: () => any, timeoutMs = 5000): Promise<any> => {
  try {
    // Create timeout promise
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error('Query timeout')), timeoutMs);
    });

    // Execute query with timeout
    const result = await Promise.race([queryFn(), timeoutPromise]);

    return result;

  } catch (error: any) {
    console.error('Query failed:', error.message);
    throw error;
  }
};

// V163.6: Simple user profile query - NEUTRALIZED
export const simpleDirectQuery = async (userId: string): Promise<{ success: boolean; data?: any; error?: string }> => {
  console.warn('⚠️ V163.6: simpleDirectQuery() - Use useUser() hook from NetworkDataContext instead');

  // Components should use: const user = useUser(userId);
  // This prevents duplicate queries to users table
  return { success: false, error: 'Use NetworkDataContext hooks instead' };
};

// V330: Enhanced Google OAuth with forced account selection
export const signInWithGoogle = async () => {
  try {
    // V212.1: Use platform-aware redirect URL helper
    const { getOAuthRedirectTo } = await import('@/lib/auth-helpers');
    const redirectURL = getOAuthRedirectTo();

    // V330: Force Google account selection to prevent cached account issues
    const { data, error } = await baseSupabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: redirectURL,
        queryParams: {
          access_type: 'offline',
          prompt: 'select_account' // V330: Force account picker
        }
      }
    });

    if (error) {
      console.error('OAuth error:', error);
      return { data, error };
    }

    return { data, error };

  } catch (e: any) {
    console.error('OAuth exception:', e);
    return { data: null, error: e };
  }
};

// Simplified session check
export const checkSession = async () => {
  try {
    const { data: { session }, error } = await baseSupabase.auth.getSession();

    if (error) {
      console.error('Session error:', error);
      return { session: null, error };
    }

    return { session, error: null };

  } catch (error: any) {
    console.error('Session check exception:', error);
    return { session: null, error: error.message };
  }
};

// Simplified session getter
export const getSession = async () => {
  try {
    const result = await baseSupabase.auth.getSession();
    return result;
  } catch (e: any) {
    console.error('Session error:', e);
    return { data: { session: null }, error: e };
  }
};

// Simplified auth state listener
export const onAuthStateChange = (callback: (event: string, session: any) => void) => {
  try {
    return baseSupabase.auth.onAuthStateChange(callback);
  } catch (e: any) {
    console.error('❌ AUTH-FIX: Auth state change error:', e);
    return { data: { subscription: { unsubscribe: () => {} } } };
  }
};

// Simplified authenticated query
export const authenticatedQuery = async <T = any>(
  tableName: string,
  selectFields: string = '*',
  filters?: { column: string; value: any; operator?: string }[]
): Promise<{ data: T[] | null; error: any }> => {
  try {
    let query = baseSupabase.from(tableName).select(selectFields);

    if (filters) {
      filters.forEach(filter => {
        const operator = filter.operator || 'eq';
        query = query[operator](filter.column, filter.value);
      });
    }

    const result = await query;

    return result as { data: T[]; error: any; };
  } catch (error) {
    console.error(`❌ AUTH-FIX: Query exception:`, error);
    return { data: null, error };
  }
};

// CRITICAL FIX: Ultra-simplified user profile getter
// V163.6: Simple user profile query - NEUTRALIZED
export const getSimpleUserProfileOriginal = async (userId: string) => {
  console.warn('⚠️ V163.6: getSimpleUserProfile() - Use useUser() hook from NetworkDataContext instead');

  // Components should use: const user = useUser(userId);
  // This prevents duplicate queries to users table
  return { data: null, error: 'Use NetworkDataContext hooks instead' };
};

// Simplified authenticated connectivity test
export const testAuthenticatedConnectivity = async () => {
  try {
    const { data: { session }, error: sessionError } = await baseSupabase.auth.getSession();

    if (sessionError || !session) {
      return {
        success: false,
        error: 'No valid session',
        details: { sessionError: sessionError?.message }
      };
    }

    const { data, error } = await baseSupabase.from('users').select('id').limit(1);

    return {
      success: !error,
      error: error?.message || null,
      details: {
        hasSession: !!session,
        userId: session.user.id,
        queryWorked: !error
      }
    };
  } catch (error: any) {
    return {
      success: false,
      error: error.message,
      details: { exception: true }
    };
  }
};

// Authenticated user profile getter (for backward compatibility)
export const getAuthenticatedUserProfile = async (userId: string) => {
  try {
    const { data, error } = await baseSupabase
      .from('users')
      .select('*')
      .eq('id', userId)
      .maybeSingle();

    if (error) {
      console.error('Authenticated profile query error:', error);
      return { data: null, error };
    }

    return { data, error: null };
  } catch (error: any) {
    console.error('Authenticated profile query exception:', error);
    return { data: null, error };
  }
};

// Export the simplified client
export { supabase };
export const getSupabaseClient = () => supabase;

// Simplified Supabase client module loaded
